import React from 'react'
import { CSSObject } from '@emotion/react'

import { Box, DisplayPosition, Image, withCitrusTheme } from '@gousto-internal/citrus-react'

import heroBackgroundDesktop from '../../../../assets/HeroAssets/hero-desktop.jpg'
import heroBackgroundDesktopWebp from '../../../../assets/HeroAssets/hero-desktop.webp'
import heroBackgroundMobileTablet from '../../../../assets/HeroAssets/hero-kitchen.jpg'
import heroBackgroundMobileTabletWebp from '../../../../assets/HeroAssets/hero-kitchen.webp'

const BACKGROUND_ALT =
  'A kitchen counter with a Gousto box overflowing with fresh ingredients and a plated meal'

// Two source crops of the hero: the portrait "kitchen" crop for mobile + tablet (<1024px) and the
// wide landscape crop for desktop (>=1024px). A single <img> carries all the per-breakpoint framing
// via 4-value responsiveStyle arrays [base(<640), 640+, 768+ (tablet), 1024+ (desktop)]; the crop
// itself is chosen by media-gated <source>s (see below) so only ONE asset downloads per viewport —
// <picture> source selection happens at parse time and honours `media`, unlike CSS display (which
// still fetched every crop — B20-1193 / Codex P2 on PR #12254).
//
// Framing per band, carried over verbatim from the previous three-image implementation:
// - Mobile (base + 640–767, Figma 183:180): the kitchen crop is scaled up (width 272%) and shifted
//   left (left -83%) to frame the box, positioned absolutely rather than via object-fit cover.
//   `top` keeps the top covered in our 812px hero. Small phones (<=420px) zoom out a touch and shift
//   the image down so the box + produce clear the headline; HeroRoot's dark bg covers the freed strip.
// - Tablet (768–1023, Figma 183:134/135): object-fit cover, centred, with translateY(64px) scale(1.14)
//   to open the ~64px headline↔box gap; the down-shift lifts the image off the top edge (HeroRoot fills).
// - Desktop (>=1024): object-fit cover, centred.
const HeroBackgroundImage = withCitrusTheme(Image, (theme) => [
  {
    position: 'absolute',
    maxWidth: 'none',
    objectFit: 'cover',
    objectPosition: '50% 50%',
    // Only affects the tablet band, which is the only band with a transform.
    transformOrigin: '50% 20%',
    // At the narrow desktop band (1024–1279, e.g. iPad Pro landscape) the hero container is close to
    // square, so object-fit cover crops the desktop asset heavily on the sides and the box sits too
    // far left, under the headline. Lowering objectPosition X shows more of the image's left and
    // pushes the box right, clear of the text. Wider desktop (>=1280) keeps 50% (box already clear).
    '@media (min-width: 1024px) and (max-width: 1279px)': {
      objectPosition: '25% 50%',
    },
  } as CSSObject,
  theme.responsiveStyle(['272%', '272%', '100%', '100%'], ['width']) as CSSObject,
  theme.responsiveStyle(['auto', 'auto', '100%', '100%'], ['height']) as CSSObject,
  theme.responsiveStyle(['-83%', '-83%', '0px', '0px'], ['left']) as CSSObject,
  theme.responsiveStyle(['-5px', '-5px', '0px', '0px'], ['top']) as CSSObject,
  theme.responsiveStyle(
    ['none', 'none', 'translateY(64px) scale(1.14)', 'none'],
    ['transform'],
  ) as CSSObject,
  // Small-phone (<=420px) sub-tuning for the mobile crop — a 5th sub-band that doesn't fit the
  // 4-value responsiveStyle model, so it stays a nested max-width media block. Placed last so it
  // wins over the base values at <=420px (the >=640 min-width queries never apply there, so no clash).
  {
    '@media (max-width: 420px)': {
      width: '290%',
      left: '-95%',
      top: '60px',
    },
  } as CSSObject,
])

// Desktop (>=1024): horizontal, dark on the left behind the left-aligned content. Mobile (base +
// 640–767) + tablet (768–1023): vertical, dark top + bottom behind the top-pinned header and
// bottom-pinned offer. The 640–767 band keeps the mobile gradient so it stays consistent with the
// mobile crop + headline in that band (tablet framing starts at 768).
const MOBILE_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(7,9,11,0) 32.21%, rgba(7,9,11,0) 75%, rgba(0,0,0,0.65) 100%)'
const TABLET_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 7.33%, rgba(7,9,11,0) 45.32%, rgba(7,9,11,0) 80.84%, rgba(0,0,0,0.65) 100%)'
const DESKTOP_GRADIENT = 'linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 61.8%)'
const GradientOverlay = withCitrusTheme(Box, (theme) =>
  theme.responsiveStyle(
    [MOBILE_GRADIENT, MOBILE_GRADIENT, TABLET_GRADIENT, DESKTOP_GRADIENT],
    ['background'],
  ),
)

// The hero background: a single <picture> whose media-gated <source>s serve the desktop crop at
// >=1024px and the kitchen crop below it, so only the matched crop is fetched. Both crops offer a
// ~50%-smaller WebP with a JPEG fallback. The <img>'s own `src` is the kitchen JPEG — the <1024px
// default — because the fallback `src` also downloads, so it must be the small-viewport asset (gating
// only <source> is not enough). display:contents keeps the <picture> from adding a layout box.
export function CWCHeroBackground() {
  return (
    <>
      <picture style={{ display: 'contents' }}>
        <source media="(min-width: 1024px)" type="image/webp" srcSet={heroBackgroundDesktopWebp} />
        <source media="(min-width: 1024px)" srcSet={heroBackgroundDesktop} />
        <source type="image/webp" srcSet={heroBackgroundMobileTabletWebp} />
        <HeroBackgroundImage src={heroBackgroundMobileTablet} alt={BACKGROUND_ALT} />
      </picture>
      <GradientOverlay
        position={DisplayPosition.Absolute}
        top="0"
        left="0"
        width="100%"
        height="100%"
      />
    </>
  )
}
